import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';
const DEFAULT_KNOWLEDGE_BASES = [
  { name: 'A3 Finance & Salaire', description: '' },
  { name: 'BauBit', description: 'Documents BauBit' },
];

function findRequestedConversation(conversations, locationSearch) {
  const requestedConversationId = new URLSearchParams(locationSearch).get('conv');
  if (!requestedConversationId) return null;
  return conversations.find((conversation) => conversation.id === requestedConversationId) || null;
}

function getNextSelectedKnowledgeBase(current, knowledgeBases) {
  return current || knowledgeBases[0] || null;
}

async function createMissingKnowledgeBases() {
  const existing = await axios.get(`${API}/knowledge-bases`);
  const missingKnowledgeBases = DEFAULT_KNOWLEDGE_BASES.filter(
    (knowledgeBase) => !existing.data.some((item) => item.name === knowledgeBase.name)
  );

  if (missingKnowledgeBases.length === 0) {
    return existing.data;
  }

  const createdKnowledgeBases = await Promise.all(
    missingKnowledgeBases.map(async (knowledgeBase) => {
      const response = await axios.post(`${API}/knowledge-bases`, knowledgeBase);
      return response.data;
    })
  );
  toast.success('Bases de connaissances créées');
  return [...existing.data, ...createdKnowledgeBases];
}

async function fetchKnowledgeBases(setKnowledgeBases, setSelectedKB, setLoading) {
  try {
    const response = await axios.get(`${API}/knowledge-bases`);
    const allKnowledgeBases = response.data.length === 0
      ? await createMissingKnowledgeBases()
      : response.data;
    setKnowledgeBases(allKnowledgeBases);
    setSelectedKB((current) => getNextSelectedKnowledgeBase(current, allKnowledgeBases));
  } catch {
    toast.error('Erreur de chargement des bases de connaissances');
  } finally {
    setLoading(false);
  }
}

async function fetchConversations(kbId, setConversations) {
  try {
    const response = await axios.get(`${API}/conversations/${kbId}`);
    setConversations(response.data);
  } catch {
    setConversations([]);
  }
}

function useKnowledgeBaseLoader() {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [selectedKB, setSelectedKB] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadKnowledgeBases = useCallback(
    () => fetchKnowledgeBases(setKnowledgeBases, setSelectedKB, setLoading),
    []
  );

  useEffect(() => {
    void loadKnowledgeBases();
  }, [loadKnowledgeBases]);

  return {
    knowledgeBases,
    selectedKB,
    setSelectedKB,
    loading,
    loadKnowledgeBases,
  };
}

function useConversationSelection(conversations, locationSearch, setSelectedConversation) {
  const matchedConversation = useMemo(
    () => findRequestedConversation(conversations, locationSearch),
    [conversations, locationSearch]
  );

  useEffect(() => {
    if (matchedConversation) {
      setSelectedConversation(matchedConversation);
    }
  }, [matchedConversation, setSelectedConversation]);
}

function useConversationData(selectedKnowledgeBaseId, locationSearch) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);

  const loadConversations = useCallback(
    (kbId) => fetchConversations(kbId, setConversations),
    []
  );

  useConversationSelection(conversations, locationSearch, setSelectedConversation);

  useEffect(() => {
    if (!selectedKnowledgeBaseId) {
      return;
    }
    void loadConversations(selectedKnowledgeBaseId);
  }, [loadConversations, selectedKnowledgeBaseId]);

  const handleNewConversation = useCallback(() => {
    setSelectedConversation(null);
  }, []);

  const handleConversationCreated = useCallback((id) => {
    if (selectedKnowledgeBaseId) {
      void loadConversations(selectedKnowledgeBaseId);
    }
    setSelectedConversation({ id });
  }, [loadConversations, selectedKnowledgeBaseId]);

  return {
    conversations,
    selectedConversation,
    setSelectedConversation,
    handleNewConversation,
    handleConversationCreated,
  };
}

export function useDashboardData(locationSearch) {
  const { knowledgeBases, selectedKB, setSelectedKB, loading, loadKnowledgeBases } = useKnowledgeBaseLoader();
  const selectedKnowledgeBaseId = selectedKB?.id || null;
  const {
    conversations,
    selectedConversation,
    setSelectedConversation,
    handleNewConversation,
    handleConversationCreated,
  } = useConversationData(selectedKnowledgeBaseId, locationSearch);

  return {
    knowledgeBases,
    selectedKB,
    setSelectedKB,
    conversations,
    selectedConversation,
    setSelectedConversation,
    loading,
    loadKnowledgeBases,
    handleNewConversation,
    handleConversationCreated,
  };
}
